import { Navigate, Outlet, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ShieldAlert, Hourglass } from 'lucide-react';
import { useAuth } from '../lib/auth/AuthContext';
import { ROLE_META } from '../lib/constants';
import type { UserDoc } from '../lib/types';

export function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', bounce: 0.3, duration: 0.5 }}
        className="flex flex-col items-center gap-3"
      >
        <span className="grid size-16 place-items-center rounded-none bg-accent text-[28px] font-black text-white shadow-none">
          B
        </span>
        <span className="text-[14px] font-semibold text-muted">Bimbingan</span>
      </motion.div>
    </div>
  );
}

/** Shown when a Firestore `admin` user lacks the required `admin` custom claim. */
export function AdminNotRecognized() {
  const { signOut } = useAuth();
  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <div className="flex max-w-sm flex-col items-center text-center">
        <span className="grid size-14 place-items-center rounded-none bg-danger-soft text-danger">
          <ShieldAlert className="size-7" />
        </span>
        <h1 className="display mt-4 text-[20px] font-bold text-ink">Akses admin tidak aktif</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">
          Peran admin hanya diakui jika akun Anda memiliki custom claim{' '}
          <code className="rounded-none bg-surface-2 px-1.5 py-0.5 text-[12px] font-semibold text-ink">
            admin
          </code>{' '}
          dari Firebase Authentication. Hubungi administrator untuk mengaktifkannya.
        </p>
        <button
          onClick={() => void signOut()}
          className="mt-6 rounded-none accent-aurora px-6 py-2.5 text-[14px] font-semibold text-white press"
        >
          Keluar
        </button>
      </div>
    </div>
  );
}

/** Shown while a dosen/staf account awaits admin approval from the user panel. */
export function PendingApproval({ user }: { user: UserDoc }) {
  const { signOut } = useAuth();
  const roleMeta = ROLE_META[user.role];
  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <div className="flex max-w-sm flex-col items-center text-center">
        <span className="grid size-14 place-items-center rounded-none bg-warning-soft text-warning">
          <Hourglass className="size-7" />
        </span>
        <h1 className="display mt-4 text-[20px] font-bold text-ink">Menunggu persetujuan</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted">
          Akun Anda terdaftar sebagai{' '}
          <span className="font-semibold text-ink">{roleMeta.label}</span>. Seorang administrator
          harus mengaktifkan akun Anda pada panel pengguna sebelum Anda dapat masuk.
        </p>
        <button
          onClick={() => void signOut()}
          className="mt-6 rounded-none accent-aurora px-6 py-2.5 text-[14px] font-semibold text-white press"
        >
          Keluar
        </button>
      </div>
    </div>
  );
}

/** Redirects "/" to the right destination based on auth state. */
export function RootRedirect() {
  const { status, user, isAdmin, claimsReady } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'signedOut') return <Navigate to="/login" replace />;
  if (status === 'unverified') return <Navigate to="/verify-email" replace />;
  if (status === 'onboarding' || !user) return <Navigate to="/onboarding" replace />;
  if (user.status === 'pending') return <PendingApproval user={user} />;
  if (user.role === 'admin' && !claimsReady) return <Splash />;
  if (user.role === 'admin' && !isAdmin) return <AdminNotRecognized />;
  return <Navigate to={`/app/${user.role}`} replace />;
}

/** Blocks routes behind sign-in. */
export function RequireAuth() {
  const { status, user } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'signedOut') return <Navigate to="/login" replace />;
  if (status === 'unverified') return <Navigate to="/verify-email" replace />;
  if (status === 'onboarding') return <Navigate to="/onboarding" replace />;
  if (user && user.status === 'pending') return <PendingApproval user={user} />;
  return <Outlet />;
}

/**
 * Ensures the `:role` path segment matches the signed-in user's actual role.
 * Prevents cross-role access through the URL. Admin routes additionally
 * require the `admin` custom claim.
 */
export function RoleRoute() {
  const { role } = useParams();
  const { ctx, isAdmin, claimsReady } = useAuth();
  const actual = ctx?.user.role;
  if (!actual) return <Splash />;
  if (ctx?.user.status === 'pending') return <PendingApproval user={ctx.user} />;
  if (role === 'admin' && !claimsReady) return <Splash />;
  if (role === 'admin' && !isAdmin) return <AdminNotRecognized />;
  if (role !== actual) return <Navigate to={`/app/${actual}`} replace />;
  return <Outlet />;
}
